import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  LinearProgress,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Tooltip,
  Paper,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  TrendingUp as TrendingUpIcon,
  TrackChanges as GoalIcon,
  CheckCircle as CheckCircleIcon,
  Timer as TimerIcon,
  AttachMoney as MoneyIcon,
  Home as HomeIcon,
  DirectionsCar as CarIcon,
  School as EducationIcon,
  Flight as VacationIcon,
  LocalHospital as HealthIcon,
  Business as BusinessIcon,
  Savings as SavingsIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, differenceInDays, differenceInMonths, parseISO } from 'date-fns';
import { Line } from 'react-chartjs-2';
import { fetchGoals, createGoal, updateGoal, deleteGoal } from '../store/slices/goalSlice';

const goalCategories = [
  { value: 'emergency', label: 'Emergency Fund', icon: <SavingsIcon />, color: '#f44336' },
  { value: 'home', label: 'Home Purchase', icon: <HomeIcon />, color: '#2196f3' },
  { value: 'car', label: 'Vehicle', icon: <CarIcon />, color: '#ff9800' },
  { value: 'education', label: 'Education', icon: <EducationIcon />, color: '#9c27b0' },
  { value: 'vacation', label: 'Vacation', icon: <VacationIcon />, color: '#00bcd4' },
  { value: 'health', label: 'Health', icon: <HealthIcon />, color: '#4caf50' },
  { value: 'business', label: 'Business', icon: <BusinessIcon />, color: '#795548' },
  { value: 'other', label: 'Other', icon: <GoalIcon />, color: '#607d8b' },
];

const Goals = () => {
  const dispatch = useDispatch();
  const { goals, loading, error } = useSelector(state => ({
    goals: state.goals.goals,
    loading: state.goals.loading.goals,
    error: state.goals.error
  }));
  
  const [open, setOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [contributionDialog, setContributionDialog] = useState(false);
  const [contributionAmount, setContributionAmount] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    target_amount: '',
    current_amount: '0',
    target_date: null,
    category: 'other',
    priority: 'medium',
  });

  useEffect(() => {
    dispatch(fetchGoals());
  }, [dispatch]);

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.target_amount) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }

    try {
      const goalData = {
        ...formData,
        target_amount: parseFloat(formData.target_amount),
        current_amount: parseFloat(formData.current_amount) || 0,
        target_date: formData.target_date ? formData.target_date.toISOString() : null,
      };

      if (editingGoal) {
        await dispatch(updateGoal({ id: editingGoal.id, ...goalData }));
        showSnackbar('Goal updated successfully', 'success');
      } else {
        await dispatch(createGoal(goalData));
        showSnackbar('Goal created successfully', 'success');
      }
      
      handleClose();
    } catch (error) {
      showSnackbar('Error saving goal', 'error');
    }
  };

  const handleEdit = (goal) => {
    setEditingGoal(goal);
    setFormData({
      name: goal.name,
      description: goal.description || '',
      target_amount: goal.target_amount.toString(),
      current_amount: goal.current_amount.toString(),
      target_date: goal.target_date ? new Date(goal.target_date) : null,
      category: goal.category || 'other',
      priority: goal.priority || 'medium',
    });
    setOpen(true);
  };

  const handleDelete = async (goalId) => {
    try {
      await dispatch(deleteGoal(goalId));
      showSnackbar('Goal deleted successfully', 'success');
    } catch (error) {
      showSnackbar('Error deleting goal', 'error');
    }
    setAnchorEl(null);
  };

  const handleAddContribution = async () => {
    if (!contributionAmount || parseFloat(contributionAmount) <= 0) {
      showSnackbar('Please enter a valid amount', 'error');
      return;
    }

    try {
      const newAmount = selectedGoal.current_amount + parseFloat(contributionAmount);
      await dispatch(updateGoal({ 
        id: selectedGoal.id, 
        current_amount: newAmount 
      }));
      
      showSnackbar(`Added $${parseFloat(contributionAmount).toLocaleString()} to ${selectedGoal.name}`, 'success');
      setContributionDialog(false);
      setContributionAmount('');
      setSelectedGoal(null);
    } catch (error) {
      showSnackbar('Error adding contribution', 'error');
    }
  };

  const handleClose = () => {
    setOpen(false);
    setEditingGoal(null);
    setFormData({
      name: '',
      description: '',
      target_amount: '',
      current_amount: '0',
      target_date: null,
      category: 'other',
      priority: 'medium',
    });
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const getProgressPercentage = (current, target) => {
    return Math.min((current / target) * 100, 100);
  };

  const getDaysRemaining = (targetDate) => {
    if (!targetDate) return null;
    const days = differenceInDays(new Date(targetDate), new Date());
    return days > 0 ? days : 0;
  };

  const getMonthsToTarget = (targetDate) => {
    if (!targetDate) return null;
    return differenceInMonths(new Date(targetDate), new Date());
  };

  const getGoalStatus = (goal) => {
    const progress = getProgressPercentage(goal.current_amount, goal.target_amount);
    const daysLeft = getDaysRemaining(goal.target_date);
    
    if (progress >= 100) return { status: 'completed', color: 'success', label: 'Completed' };
    if (daysLeft === 0) return { status: 'overdue', color: 'error', label: 'Overdue' };
    if (daysLeft <= 30) return { status: 'urgent', color: 'warning', label: 'Urgent' };
    return { status: 'active', color: 'primary', label: 'Active' };
  };

  const getCategoryInfo = (category) => {
    return goalCategories.find(cat => cat.value === category) || goalCategories[goalCategories.length - 1];
  };

  const getGoalsStats = () => {
    const total = goals.length;
    const completed = goals.filter(goal => getProgressPercentage(goal.current_amount, goal.target_amount) >= 100).length;
    const totalTargetAmount = goals.reduce((sum, goal) => sum + goal.target_amount, 0);
    const totalCurrentAmount = goals.reduce((sum, goal) => sum + goal.current_amount, 0);
    const overallProgress = totalTargetAmount > 0 ? (totalCurrentAmount / totalTargetAmount) * 100 : 0;

    return { total, completed, totalTargetAmount, totalCurrentAmount, overallProgress };
  };

  const stats = getGoalsStats();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Financial Goals
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpen(true)}
            sx={{ borderRadius: 2 }}
          >
            Add Goal
          </Button>
        </Box>

        {/* Stats Overview */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={3}>
            <Card sx={{ textAlign: 'center', p: 2, background: 'linear-gradient(45deg, #2196F3, #21CBF3)' }}>
              <Typography variant="h3" sx={{ color: 'white', fontWeight: 'bold' }}>
                {stats.total}
              </Typography>
              <Typography variant="body1" sx={{ color: 'white' }}>
                Total Goals
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ textAlign: 'center', p: 2, background: 'linear-gradient(45deg, #4CAF50, #8BC34A)' }}>
              <Typography variant="h3" sx={{ color: 'white', fontWeight: 'bold' }}>
                {stats.completed}
              </Typography>
              <Typography variant="body1" sx={{ color: 'white' }}>
                Completed
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ textAlign: 'center', p: 2, background: 'linear-gradient(45deg, #FF9800, #FFC107)' }}>
              <Typography variant="h3" sx={{ color: 'white', fontWeight: 'bold' }}>
                ${stats.totalCurrentAmount.toLocaleString()}
              </Typography>
              <Typography variant="body1" sx={{ color: 'white' }}>
                Total Saved
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ textAlign: 'center', p: 2, background: 'linear-gradient(45deg, #9C27B0, #E91E63)' }}>
              <Typography variant="h3" sx={{ color: 'white', fontWeight: 'bold' }}>
                {stats.overallProgress.toFixed(0)}%
              </Typography>
              <Typography variant="body1" sx={{ color: 'white' }}>
                Overall Progress
              </Typography>
            </Card>
          </Grid>
        </Grid>

        {/* Overall Progress */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Overall Goal Progress
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ flexGrow: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={stats.overallProgress}
                  sx={{ 
                    height: 12, 
                    borderRadius: 6,
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 6,
                    }
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ minWidth: 60 }}>
                {stats.overallProgress.toFixed(1)}%
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              ${stats.totalCurrentAmount.toLocaleString()} of ${stats.totalTargetAmount.toLocaleString()} total target
            </Typography>
          </CardContent>
        </Card>

        {/* Goals List */}
        {goals.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 8 }}>
              <GoalIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                No goals yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Start by creating your first financial goal
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpen(true)}
              >
                Create Your First Goal
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {goals.map((goal) => {
              const progress = getProgressPercentage(goal.current_amount, goal.target_amount);
              const daysLeft = getDaysRemaining(goal.target_date);
              const monthsLeft = getMonthsToTarget(goal.target_date);
              const goalStatus = getGoalStatus(goal);
              const categoryInfo = getCategoryInfo(goal.category);
              const remaining = goal.target_amount - goal.current_amount;
              const monthlyRequired = monthsLeft > 0 ? remaining / monthsLeft : 0;

              return (
                <Grid item xs={12} md={6} lg={4} key={goal.id}>
                  <Card sx={{ 
                    height: '100%', 
                    position: 'relative',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                    },
                    transition: 'all 0.3s ease-in-out',
                  }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ bgcolor: categoryInfo.color, width: 40, height: 40 }}>
                            {categoryInfo.icon}
                          </Avatar>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {goal.name}
                            </Typography>
                            <Chip
                              label={goalStatus.label}
                              color={goalStatus.color}
                              size="small"
                              variant="filled"
                            />
                          </Box>
                        </Box>
                        <IconButton
                          onClick={(e) => {
                            setAnchorEl(e.currentTarget);
                            setSelectedGoal(goal);
                          }}
                          size="small"
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Box>

                      {goal.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {goal.description}
                        </Typography>
                      )}

                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Progress
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {progress.toFixed(1)}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={progress}
                          sx={{ 
                            height: 8, 
                            borderRadius: 4,
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 4,
                              background: `linear-gradient(45deg, ${categoryInfo.color}, ${categoryInfo.color}aa)`,
                            }
                          }}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Current
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            ${goal.current_amount.toLocaleString()}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" color="text.secondary">
                            Target
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            ${goal.target_amount.toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>

                      {goal.target_date && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Target Date: {format(new Date(goal.target_date), 'MMM dd, yyyy')}
                          </Typography>
                          {daysLeft > 0 && (
                            <Typography variant="body2" color="text.secondary">
                              {daysLeft} days remaining
                            </Typography>
                          )}
                          {monthlyRequired > 0 && progress < 100 && (
                            <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                              Need ${monthlyRequired.toLocaleString()} per month
                            </Typography>
                          )}
                        </Box>
                      )}

                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          fullWidth
                          onClick={() => {
                            setSelectedGoal(goal);
                            setContributionDialog(true);
                          }}
                          disabled={progress >= 100}
                        >
                          Add Money
                        </Button>
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => handleEdit(goal)}
                        >
                          Edit
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* Goal Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          <MenuItem onClick={() => handleEdit(selectedGoal)}>
            <EditIcon sx={{ mr: 1 }} />
            Edit Goal
          </MenuItem>
          <MenuItem
            onClick={() => {
              setContributionDialog(true);
              setAnchorEl(null);
            }}
          >
            <MoneyIcon sx={{ mr: 1 }} />
            Add Contribution
          </MenuItem>
          <MenuItem
            onClick={() => handleDelete(selectedGoal?.id)}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon sx={{ mr: 1 }} />
            Delete Goal
          </MenuItem>
        </Menu>

        {/* Add/Edit Goal Dialog */}
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingGoal ? 'Edit Goal' : 'Create New Goal'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                label="Goal Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                fullWidth
                required
              />
              
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                fullWidth
                multiline
                rows={3}
              />
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Target Amount"
                    value={formData.target_amount}
                    onChange={(e) => setFormData({ ...formData, target_amount: e.target.value })}
                    type="number"
                    fullWidth
                    required
                    InputProps={{
                      startAdornment: '$',
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Current Amount"
                    value={formData.current_amount}
                    onChange={(e) => setFormData({ ...formData, current_amount: e.target.value })}
                    type="number"
                    fullWidth
                    InputProps={{
                      startAdornment: '$',
                    }}
                  />
                </Grid>
              </Grid>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={formData.category}
                      label="Category"
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    >
                      {goalCategories.map((category) => (
                        <MenuItem key={category.value} value={category.value}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {category.icon}
                            {category.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value={formData.priority}
                      label="Priority"
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    >
                      <MenuItem value="low">Low</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              
              <DatePicker
                label="Target Date"
                value={formData.target_date}
                onChange={(date) => setFormData({ ...formData, target_date: date })}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained">
              {editingGoal ? 'Update Goal' : 'Create Goal'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Contribution Dialog */}
        <Dialog open={contributionDialog} onClose={() => setContributionDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add Contribution</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Adding money to: <strong>{selectedGoal?.name}</strong>
              </Typography>
              <TextField
                label="Contribution Amount"
                value={contributionAmount}
                onChange={(e) => setContributionAmount(e.target.value)}
                type="number"
                fullWidth
                InputProps={{
                  startAdornment: '$',
                }}
                autoFocus
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setContributionDialog(false)}>Cancel</Button>
            <Button onClick={handleAddContribution} variant="contained">
              Add Contribution
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default Goals; 